import { useState } from 'react';
import { BEACON_METRICS, DR_LARKIN_ENDORSEMENT } from '@/lib/mockData';
import { Award, ArrowRight, TrendingDown, TrendingUp, ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function ClinicalAuthorityCard() {
  const [caseStudyOpen, setCaseStudyOpen] = useState(false);
  const { before, after } = BEACON_METRICS;

  return (
    <>
      <div className="glass-card-elevated p-5 border-primary/20 relative overflow-hidden">
        {/* Glow effect */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
        
        <div className="relative">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                <Award className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Clinical Authority Partner</h3>
                <p className="text-xs text-muted-foreground">Verified outcomes</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-1 text-xs"
              onClick={() => setCaseStudyOpen(true)}
            >
              Case Study
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>

          {/* Partner Info */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/20 flex items-center justify-center">
                <span className="text-sm font-semibold text-blue-400">DL</span>
              </div>
              <div>
                <p className="font-medium text-sm">Dr. Larkin</p>
                <p className="text-xs text-muted-foreground">Clinical Partner</p>
              </div>
            </div>
            <blockquote className="text-xs text-muted-foreground italic leading-relaxed border-l-2 border-primary/30 pl-3">
              {DR_LARKIN_ENDORSEMENT}
            </blockquote>
          </div>

          {/* Before vs After */}
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 rounded-lg bg-destructive/5 border border-destructive/10">
              <span className="text-[10px] uppercase tracking-wider text-destructive/70 font-medium">Before</span>
            </div>
            <div className="text-center p-3 rounded-lg bg-success/5 border border-success/10">
              <span className="text-[10px] uppercase tracking-wider text-success/70 font-medium">After LINK</span>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            <MetricRow 
              label="Billing touches/claim" 
              before={before.billingTouchesPerClaim} 
              after={after.billingTouchesPerClaim}
              suffix=""
              lowerIsBetter
            />
            <MetricRow 
              label="Denial rate" 
              before={before.denialRate} 
              after={after.denialRate}
              suffix="%"
              lowerIsBetter
            />
            <MetricRow 
              label="Days to cash" 
              before={before.daysToCash} 
              after={after.daysToCash}
              suffix=" days"
              lowerIsBetter
            />
            <MetricRow 
              label="Net collections" 
              before={before.netCollectionsChange} 
              after={after.netCollectionsChange}
              suffix="%"
              isPercentChange
            />
          </div>
        </div>
      </div>

      {/* Case Study Modal */}
      <Dialog open={caseStudyOpen} onOpenChange={setCaseStudyOpen}>
        <DialogContent className="sm:max-w-[600px] bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Clinical Partner Case Study
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-secondary/50">
              <h4 className="font-semibold mb-2">The Challenge</h4>
              <p className="text-sm text-muted-foreground">
                A leading orthopedic practice with 12 providers was experiencing a 14% denial rate 
                and 21-day average time to payment. Their billing team spent 12+ touches per claim 
                on manual coding review and denial follow-up.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-secondary/50">
              <h4 className="font-semibold mb-2">The Solution</h4>
              <p className="text-sm text-muted-foreground">
                LINK's AI agents now intelligently code all encounters, 
                simulate payer denials before submission, and auto-correct claims 
                to prevent rejections. The billing team now focuses on strategic 
                oversight rather than claim-by-claim review.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-success/5 border border-success/20 text-center">
                <p className="text-3xl font-bold text-success">92%</p>
                <p className="text-xs text-muted-foreground mt-1">Reduction in billing touches</p>
              </div>
              <div className="p-4 rounded-lg bg-success/5 border border-success/20 text-center">
                <p className="text-3xl font-bold text-success">78%</p>
                <p className="text-xs text-muted-foreground mt-1">Reduction in denial rate</p>
              </div>
              <div className="p-4 rounded-lg bg-success/5 border border-success/20 text-center">
                <p className="text-3xl font-bold text-success">57%</p>
                <p className="text-xs text-muted-foreground mt-1">Faster time to cash</p>
              </div>
              <div className="p-4 rounded-lg bg-success/5 border border-success/20 text-center">
                <p className="text-3xl font-bold text-success">+18%</p>
                <p className="text-xs text-muted-foreground mt-1">Net collections increase</p>
              </div>
            </div>

            <blockquote className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-4">
              {DR_LARKIN_ENDORSEMENT}
              <footer className="mt-2 text-xs text-foreground not-italic">
                — Dr. Larkin, Clinical Partner
              </footer>
            </blockquote>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MetricRow({ 
  label, 
  before, 
  after, 
  suffix = '',
  lowerIsBetter = false,
  isPercentChange = false,
}: { 
  label: string; 
  before: number; 
  after: number; 
  suffix?: string;
  lowerIsBetter?: boolean;
  isPercentChange?: boolean;
}) {
  const improved = lowerIsBetter ? after < before : after > before;
  
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono text-destructive/70 line-through">
          {isPercentChange ? `${before > 0 ? '+' : ''}${before}${suffix}` : `${before}${suffix}`}
        </span>
        <ArrowRight className="w-3 h-3 text-muted-foreground" />
        <span className="text-sm font-mono font-semibold text-success flex items-center gap-1">
          {isPercentChange ? `+${after}${suffix}` : `${after}${suffix}`}
          {improved && (lowerIsBetter ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />)}
        </span>
      </div>
    </div>
  );
}
